import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient,
  HttpBackend,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { environment } from '@base/environments/environment';
import { UtilsService } from '../../../services/utils.service';
import { LogedUser } from '../../models/logged-user.model';
import { ClientFingerprintService } from './client-fingerprint.service';
import { UserSessionService } from '../../../services/user-session.service';
import { Router } from '@angular/router';

@Injectable()
export class NewJwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(
    null,
  );
  private readonly http: HttpClient;

  constructor(
    private readonly utils: UtilsService,
    httpBackend: HttpBackend,
    private readonly fingerprintService: ClientFingerprintService,
    private readonly userSessionService: UserSessionService,
    private readonly router: Router,
  ) {
    this.http = new HttpClient(httpBackend);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isGestionAuthUrl(request.url)) {
      return next.handle(this.withAuthCredentials(request));
    }

    const CURRENT_USER: LogedUser | undefined = this.utils.getLoggedUser();
    const USES_PROXY = String(environment.apiEndpoint || '').includes('proxy');
    const IS_URL_OLD = request.url.includes(environment.oldFrontUrl);

    if (
      CURRENT_USER?.secret &&
      !IS_URL_OLD &&
      USES_PROXY &&
      this.isConfiguredProxyUrl(request.url)
    ) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${CURRENT_USER.secret}`,
        },
      });
    }

    if (IS_URL_OLD || !USES_PROXY) {
      request = request.clone({ withCredentials: true });
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 401 &&
          CURRENT_USER?.secret &&
          USES_PROXY &&
          !IS_URL_OLD
        ) {
          if (this.isRefreshing) {
            return this.waitForRefreshAndRetry(request, next);
          }
          return this.refreshAndRetry(request, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private isGestionAuthUrl(url: string): boolean {
    const AUTH_BASE = environment.authApiBaseUrl;
    return Boolean(AUTH_BASE) && url.startsWith(`${AUTH_BASE}/v1/gestion`);
  }

  private isConfiguredProxyUrl(url: string): boolean {
    const PROXY_PREFIX = String(environment.apiEndpoint || '');
    if (!PROXY_PREFIX) {
      return false;
    }
    if (url.startsWith(PROXY_PREFIX)) {
      return true;
    }
    try {
      const PROXY_ORIGIN = new URL(PROXY_PREFIX).origin;
      return Boolean(PROXY_ORIGIN) && url.startsWith(PROXY_ORIGIN);
    } catch {
      return false;
    }
  }

  private withAuthCredentials(request: HttpRequest<any>): HttpRequest<any> {
    const fingerprint = this.fingerprintService.getSync();
    return request.clone({
      withCredentials: true,
      setHeaders: fingerprint ? { fingerprint } : {},
    });
  }

  private waitForRefreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.refreshTokenSubject.pipe(
      filter((token) => token != null || !this.isRefreshing),
      take(1),
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('Session refresh failed'));
        }
        return next.handle(
          request.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
          }),
        );
      }),
    );
  }

  private refreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    const AUTH_BASE = environment.authApiBaseUrl;
    const fingerprint = this.fingerprintService.getSync();
    return this.http
      .post<{ data?: { idToken?: string } }>(
        `${AUTH_BASE}/v1/gestion/refresh`,
        {},
        {
          withCredentials: true,
          headers: fingerprint ? { fingerprint } : {},
        },
      )
      .pipe(
        switchMap((response) => {
          this.isRefreshing = false;
          const idToken = response?.data?.idToken;
          if (!idToken) {
            this.refreshTokenSubject.next(null);
            this.router.navigate(['session-expired']);
            return throwError(() => new Error('Missing refresh token'));
          }
          const currentUser = this.utils.getLoggedUser();
          if (currentUser) {
            currentUser.secret = idToken;
            this.userSessionService.loggedUser = currentUser;
          }
          this.refreshTokenSubject.next(idToken);
          return next.handle(
            request.clone({
              setHeaders: { Authorization: `Bearer ${idToken}` },
            }),
          );
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.router.navigate(['session-expired']);
          return throwError(() => error);
        }),
      );
  }
}
